import { useMemo, useState } from 'react'
import { LiveCartAudience, LiveCartItem, LiveCartViewer } from '../types'

/**
 * Carrinho do público da live — pessoa a pessoa, com o que ficou abandonado.
 *
 * O entregável desta tela é a lista que vai para o cliente: **quais produtos,
 * de quais pessoas, ficaram no carrinho depois que a janela de atribuição da
 * live fechou**. Enquanto a janela estiver aberta a tela diz isso e não chama
 * nada de abandonado — o cliente ainda pode comprar.
 *
 * ## Por que o layout é assim
 *
 * Um número grande, três de apoio. O valor abandonado é a resposta; o resto é
 * contexto. Quatro cartões do mesmo tamanho (o formato anterior) davam peso
 * igual a coisas de importância diferente e obrigavam a ler os quatro para
 * achar o que interessa.
 *
 * Método e avisos de qualidade ficam RECOLHIDOS. São indispensáveis para
 * confiar no número, e péssimos como parede de texto entre o número e a lista.
 * Os avisos aparecem como etiquetas curtas e só existem quando há o que dizer.
 *
 * Uma linha por pessoa, toda com a mesma altura: as quantidades de cada um
 * viram contagem, e os produtos moram na linha expandida. Fichas de produto
 * empilhadas na tabela faziam cada linha ter uma altura, que é a causa mais
 * comum de uma lista parecer bagunçada.
 *
 * Cor com parcimônia: âmbar só em abandono, verde só em recuperação, coral só
 * no que é da live. Linha com abandono é marcada por um filete à esquerda em
 * vez de fundo colorido — o fundo tingido empilhado em dezenas de linhas vira
 * ruído e some com a hierarquia.
 */

// ─── Tokens ──────────────────────────────────────────────────────────────────

const PROPERTY_TIME_ZONE = 'America/Sao_Paulo'

/** Escala de espaço única (múltiplos de 4) — evita margens improvisadas. */
const SPACE = { xs: 4, sm: 8, md: 12, lg: 16, xl: 20 } as const

/**
 * Tokens do painel (`styles/*.css`). Os valores de reserva espelham os reais,
 * para a seção não mudar de aparência se o CSS não tiver carregado.
 *
 * `line` é um degrau mais forte que o dos painéis vizinhos de propósito: esta
 * seção é uma tabela densa, e em `--ink-100` os separadores de linha somem.
 */
const C = {
  ink900: 'var(--ink-800, #1a1d23)',
  ink700: 'var(--ink-700, #2a2f3a)',
  ink500: 'var(--ink-500, #6b7280)',
  ink400: 'var(--ink-400, #9aa1ad)',
  ink300: 'var(--ink-300, #cdd2db)',
  line: 'var(--ink-200, #e5e8ee)',
  lineSoft: 'var(--ink-100, #f1f3f7)',
  surface: 'var(--ink-50, #f7f8fb)',
  paper: 'var(--paper, #fff)',
  /** Abandono. Usado SÓ em abandono. */
  amber: 'var(--warning, #c87800)',
  amberSoft: 'var(--warning-bg, #fff4e0)',
  /** Recuperação e compra. */
  green: 'var(--success, #0ea968)',
  greenSoft: 'var(--success-bg, #e6f7ef)',
  /** Live. */
  coral: 'var(--accent-coral, #ff7a59)',
} as const

/** Uma grade só para o cabeçalho e para as linhas — se divergirem, desalinha. */
const ROW_GRID = '28px minmax(0, 1.6fr) 120px 130px 120px 14px'

// ─── Formatação ──────────────────────────────────────────────────────────────

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value || 0)
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short', timeZone: PROPERTY_TIME_ZONE })
  } catch {
    return iso
  }
}

function formatDateHourMinute(dhm: string): string {
  if (!dhm || dhm.length < 12) return dhm
  return `${dhm.slice(6, 8)}/${dhm.slice(4, 6)}/${dhm.slice(0, 4)} ${dhm.slice(8, 10)}:${dhm.slice(10, 12)}`
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`
}

/** "1 item" / "3 itens" — o caso mais repetido da seção. */
function items(n: number): string {
  return plural(n, 'item', 'itens')
}

/**
 * Valor de um conjunto de itens que PODE ser desconhecido.
 *
 * O Google Analytics só informa o valor de item comprado, então há item sem
 * preço. Somar zero e imprimir "R$ 0,00" afirmaria que o carrinho vale nada —
 * foi exatamente o que apareceu ao lado de uma compra de R$ 61,00. Quando não
 * há valor conhecido, a seção diz que não sabe.
 */
function money(count: number, value: number): { text: string; unknown: boolean; empty: boolean } {
  if (count === 0) return { text: '—', unknown: false, empty: true }
  if (value > 0) return { text: formatCurrency(value), unknown: false, empty: false }
  return { text: 'preço não informado', unknown: true, empty: false }
}

/** Quando o item entrou, do ponto de vista da live. `~` = horário aproximado. */
function phaseLabel(item: LiveCartItem): string {
  if (item.phase === 'unconfirmed') return 'horário não confirmado'
  // `~` = balde de hora/dia; "inferida" = decidida pelo horário em que a pessoa entrou na live.
  const approx = item.timePrecision === 'minute' ? '' : '~'
  if (item.phaseConfidence === 'inferred') {
    return `${approx}${item.phase === 'during-live' ? 'durante a live' : 'antes da live'} (pelo horário em que entrou)`
  }
  if (item.phase === 'after-live') return `${approx}depois da live`
  if (item.phase === 'during-live') return `${approx}durante a live`
  const days = item.daysInCartBeforeLive ?? 0
  if (days === 0) return `${approx}antes da live, no mesmo dia`
  return `${approx}antes da live, há ${plural(days, 'dia', 'dias')}`
}

const SOURCE_LABEL: Record<LiveCartItem['source'], string> = {
  'this-live': 'nesta live',
  'other-live': 'outra live',
  video: 'vídeo',
  unknown: '',
}

const ORIGIN_LABEL: Record<LiveCartItem['origin'], string> = {
  widde: 'Widde',
  ga4: 'Analytics',
  both: 'ambos',
}

/** Situação do item — a coluna que o cliente lê. */
function itemStatus(item: LiveCartItem): { label: string; fg: string; bg: string } {
  if (item.recovered) return { label: 'recuperado', fg: C.green, bg: C.greenSoft }
  if (item.purchase) return { label: 'comprado', fg: C.green, bg: C.greenSoft }
  if (item.abandoned) return { label: 'abandonado', fg: C.amber, bg: C.amberSoft }
  if (item.stillInCart) return { label: 'no carrinho', fg: C.ink500, bg: C.surface }
  return { label: 'saiu do carrinho', fg: C.ink400, bg: C.surface }
}

// ─── CSV ─────────────────────────────────────────────────────────────────────

function csvCell(value: string | number): string {
  const s = String(value ?? '')
  return /[",;\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

/**
 * Exporta a lista que vai para o cliente. Uma linha por pessoa × produto,
 * porque é assim que o time trabalha o abandono. Só o que o filtro atual
 * mostra — o que está na tela é o que sai no arquivo.
 */
function downloadCsv(viewers: LiveCartViewer[], onlyAbandoned: boolean): void {
  const header = [
    'widde_uid', 'nome', 'contato', 'identificado',
    'produto', 'produto_id', 'quantidade', 'preco_unitario', 'valor',
    'adicionado_em', 'quando', 'precisao_do_tempo', 'origem_do_dado', 'estava_na_live',
    'situacao', 'compra_transacao', 'compra_em', 'compra_valor',
  ]
  const lines = [header.join(';')]

  for (const v of viewers) {
    for (const it of v.items) {
      if (onlyAbandoned && !it.abandoned) continue
      lines.push([
        v.widdeUid,
        v.leadName || '',
        v.leadContact || '',
        v.identified ? 'sim' : 'nao',
        it.productTitle,
        it.productId,
        it.quantity,
        it.priceSource === 'none' ? '' : it.unitPrice.toFixed(2),
        it.priceSource === 'none' ? '' : it.total.toFixed(2),
        it.firstAddedAt ? formatDateTime(it.firstAddedAt) : '',
        phaseLabel(it),
        it.timePrecision,
        ORIGIN_LABEL[it.origin],
        it.inLiveCatalog ? 'sim' : 'nao',
        itemStatus(it).label,
        it.purchase?.transactionId || '',
        it.purchase?.dateHourMinute ? formatDateHourMinute(it.purchase.dateHourMinute) : '',
        it.purchase ? it.purchase.itemRevenue.toFixed(2) : '',
      ].map(csvCell).join(';'))
    }
  }

  // BOM para o Excel abrir a acentuação correta em pt-BR.
  const blob = new Blob(['﻿' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `carrinho-live-${onlyAbandoned ? 'abandonado' : 'completo'}-${new Date().toISOString().slice(0, 10)}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Seção ───────────────────────────────────────────────────────────────────

type ViewerFilter = 'abandoned' | 'recovered' | 'with-cart' | 'identified' | 'all'

const PAGE_SIZE = 30

export function LiveCartAudienceSection({ audience }: { audience: LiveCartAudience }) {
  const { summary, viewers } = audience
  const [filter, setFilter] = useState<ViewerFilter>(summary.abandoned.items > 0 ? 'abandoned' : 'all')
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [showMethod, setShowMethod] = useState(false)

  const counts = useMemo(() => ({
    abandoned: viewers.filter((v) => v.items.some((it) => it.abandoned)).length,
    recovered: viewers.filter((v) => v.items.some((it) => it.recovered)).length,
    'with-cart': viewers.filter((v) => v.items.length > 0).length,
    identified: viewers.filter((v) => v.identified).length,
    all: viewers.length,
  }), [viewers])

  const filtered = useMemo(() => {
    let list = viewers
    if (filter === 'abandoned') list = list.filter((v) => v.items.some((it) => it.abandoned))
    else if (filter === 'recovered') list = list.filter((v) => v.items.some((it) => it.recovered))
    else if (filter === 'with-cart') list = list.filter((v) => v.items.length > 0)
    else if (filter === 'identified') list = list.filter((v) => v.identified)

    const q = search.trim().toLowerCase()
    if (!q) return list
    return list.filter((v) =>
      v.widdeUid.toLowerCase().includes(q) ||
      (v.leadName || '').toLowerCase().includes(q) ||
      (v.leadContact || '').toLowerCase().includes(q) ||
      v.items.some((it) => it.productTitle.toLowerCase().includes(q) || it.productId.toLowerCase().includes(q))
    )
  }, [viewers, filter, search])

  const visible = filtered.slice(0, page * PAGE_SIZE)
  const hasMore = visible.length < filtered.length

  const handleFilter = (f: ViewerFilter) => {
    setFilter(f)
    setPage(1)
  }

  return (
    <section style={{ borderTop: `1px solid ${C.line}` }}>
      <SectionHeader summary={summary} showMethod={showMethod} onToggleMethod={() => setShowMethod((v) => !v)} />
      {showMethod && <MethodPanel summary={summary} />}
      <Headline summary={summary} />
      <Toolbar
        filter={filter}
        counts={counts}
        onFilter={handleFilter}
        search={search}
        onSearch={(v) => { setSearch(v); setPage(1) }}
        onExport={() => downloadCsv(filtered, filter === 'abandoned')}
        exportDisabled={filtered.length === 0}
        exportOnlyAbandoned={filter === 'abandoned'}
      />

      <div style={{ maxHeight: 600, overflowY: 'auto' }}>
        <ColumnHeader />
        {filtered.length === 0 ? (
          <EmptyState hasViewers={viewers.length > 0} summary={summary} />
        ) : (
          visible.map((v) => <ViewerRow key={v.widdeUid} viewer={v} />)
        )}
        {hasMore && (
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            style={{
              width: '100%', padding: SPACE.md, fontSize: 12, background: C.surface, border: 'none',
              borderTop: `1px solid ${C.line}`, cursor: 'pointer', color: C.ink700,
            }}
          >
            Mostrar mais {Math.min(PAGE_SIZE, filtered.length - visible.length)} de {filtered.length - visible.length} restantes
          </button>
        )}
      </div>
    </section>
  )
}

// ─── Cabeçalho ───────────────────────────────────────────────────────────────

function SectionHeader({ summary, showMethod, onToggleMethod }: {
  summary: LiveCartAudience['summary']
  showMethod: boolean
  onToggleMethod: () => void
}) {
  return (
    <header style={{
      padding: `${SPACE.lg}px ${SPACE.xl}px ${SPACE.md}px`,
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: SPACE.md, flexWrap: 'wrap',
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: SPACE.sm, flexWrap: 'wrap' }}>
        {/* Título no mesmo formato dos painéis vizinhos da live. */}
        <span style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: C.ink500 }}>
          Carrinho do público
        </span>
        <span style={{ fontSize: 12, color: C.ink400 }}>
          {plural(summary.viewers, 'espectador', 'espectadores')}, {summary.viewersWithCart} com carrinho e {summary.identifiedViewers} identificados
        </span>
      </div>
      <button
        type="button"
        onClick={onToggleMethod}
        style={{
          fontSize: 11, fontWeight: 600, color: C.ink500, background: 'none',
          border: `1px solid ${C.line}`, borderRadius: 6, padding: '3px 10px', cursor: 'pointer',
        }}
      >
        {showMethod ? 'Ocultar como lemos' : 'Como lemos estes números'}
      </button>
    </header>
  )
}

/** Método, recolhido por padrão: indispensável para confiar, ruim como parede. */
function MethodPanel({ summary }: { summary: LiveCartAudience['summary'] }) {
  return (
    <div style={{
      margin: `0 ${SPACE.xl}px ${SPACE.md}px`, padding: SPACE.lg, borderRadius: 8,
      background: C.surface, border: `1px solid ${C.line}`,
      display: 'grid', gap: SPACE.md, gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      fontSize: 12, color: C.ink700, lineHeight: 1.6,
    }}>
      <Definition term="O que conta como carrinho">
        Adições feitas em qualquer lugar da loja, lidas no Google Analytics, somadas às adições feitas
        nas telas da Widde, que trazem o horário exato, o preço e onde a pessoa clicou. Procuramos
        adições nos {summary.lookbackDays} dias anteriores à live.
      </Definition>
      <Definition term="O que conta como abandonado">
        A janela de atribuição da live fechou e o item não foi comprado dentro dela. A janela é a que
        está selecionada no painel, contada a partir do fim da live e cortada pela live seguinte.
        Enquanto a janela está aberta, nada é abandonado.
      </Definition>
      <Definition term="O que conta como recuperado">
        O item já estava parado no carrinho quando a live começou, não tinha sido comprado até ali, e
        foi comprado dentro da janela.
      </Definition>
      <Definition term="De onde vem cada item">
        {summary.itemsByOrigin.both} itens apareceram nas duas fontes, {summary.itemsByOrigin.ga4} só
        no Google Analytics e {summary.itemsByOrigin.widde} só nos eventos da Widde.
      </Definition>
    </div>
  )
}

function Definition({ term, children }: { term: string; children: React.ReactNode }) {
  return (
    <div>
      <div style={{ fontWeight: 700, color: C.ink900, marginBottom: 2 }}>{term}</div>
      <div style={{ maxWidth: '72ch' }}>{children}</div>
    </div>
  )
}

// ─── Números ─────────────────────────────────────────────────────────────────

/**
 * Um número grande e três de apoio. O valor abandonado é a resposta da seção;
 * recuperado, comprado e catálogo são contexto para interpretá-lo.
 */
function Headline({ summary }: { summary: LiveCartAudience['summary'] }) {
  const closed = summary.windowClosed
  const lead = closed ? summary.abandoned : summary.pending
  const leadMoney = money(lead.items, lead.value)

  return (
    <div style={{
      margin: `0 ${SPACE.xl}px ${SPACE.lg}px`,
      display: 'grid', gridTemplateColumns: 'minmax(240px, 1fr) minmax(280px, 1.1fr)', gap: SPACE.xl,
      alignItems: 'start',
    }}>
      {/* Número-chave, marcado por um filete e não por um cartão colorido. */}
      <div style={{ borderLeft: `3px solid ${closed ? C.amber : C.ink300}`, paddingLeft: SPACE.lg }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: closed ? C.amber : C.ink500 }}>
          {closed ? 'Carrinho abandonado' : 'Janela de atribuição ainda aberta'}
        </div>
        {/* Sem preço conhecido, a contagem vira o número — nunca "R$ 0,00". */}
        <div style={{
          fontSize: leadMoney.unknown ? 24 : 30, fontWeight: 800, color: C.ink900,
          lineHeight: 1.15, letterSpacing: -0.5, marginTop: 2,
        }}>
          {leadMoney.unknown ? items(lead.items) : leadMoney.text}
          {!leadMoney.unknown && summary.cartValueIsPartial && (
            <span title="Há itens sem preço, então o total é um piso" style={{ fontSize: 16, color: C.ink400 }}>+</span>
          )}
        </div>
        <div style={{ fontSize: 12, color: C.ink500, marginTop: SPACE.xs, lineHeight: 1.5 }}>
          {closed ? (
            <>
              {leadMoney.unknown
                ? `De ${plural(lead.viewers, 'pessoa', 'pessoas')}. A loja não informou o preço destes itens.`
                : `${items(lead.items)} de ${plural(lead.viewers, 'pessoa', 'pessoas')}.`}
              {summary.abandonedInLiveCatalog.items > 0 && ` ${summary.abandonedInLiveCatalog.items} estavam no catálogo da live.`}
              <div style={{ color: C.ink400, marginTop: 2 }}>Janela fechou em {formatDateTime(summary.windowEndsAt)}.</div>
            </>
          ) : (
            <>
              {items(lead.items)} ainda no carrinho podem virar compra.
              <div style={{ color: C.ink400, marginTop: 2 }}>O veredito sai em {formatDateTime(summary.windowEndsAt)}.</div>
            </>
          )}
        </div>
        {/* As ressalvas qualificam ESTE número, então moram junto dele. */}
        <QualityFlags summary={summary} />
      </div>

      {/* Apoio: lista de valores, não cartões — peso menor, leitura em coluna. */}
      <dl style={{ margin: 0, display: 'grid', gap: 0, alignSelf: 'center' }}>
        <SupportStat
          term="Recuperados pela live"
          value={formatCurrency(summary.recovered.revenue)}
          detail={summary.recovered.items > 0
            ? `${plural(summary.recovered.items, 'item parado', 'itens parados')} antes da live`
            : `nenhum dos ${plural(summary.pendingBeforeLive.items, 'item parado', 'itens parados')}`}
          accent={summary.recovered.items > 0 ? C.green : undefined}
        />
        <SupportStat
          term="Comprados na janela"
          value={formatCurrency(summary.revenuePurchasedInWindow)}
          detail={`${summary.itemsPurchasedInWindow} de ${items(summary.cartItems)} no carrinho`}
        />
        <SupportStat
          term="Itens da live no carrinho"
          value={`${summary.itemsInLiveCatalog} de ${summary.cartItems}`}
          detail="bateram com o catálogo desta live"
          accent={summary.itemsInLiveCatalog > 0 ? C.coral : undefined}
        />
      </dl>
    </div>
  )
}

function SupportStat({ term, value, detail, accent }: { term: string; value: string; detail: string; accent?: string }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: SPACE.md,
      padding: `${SPACE.sm}px 0`, borderBottom: `1px solid ${C.lineSoft}`,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: C.ink700 }}>{term}</div>
        <div style={{ fontSize: 11, color: C.ink400, marginTop: 1 }}>{detail}</div>
      </div>
      <div style={{ fontSize: 15, fontWeight: 700, color: accent ?? C.ink900, whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  )
}

// ─── Avisos de qualidade ─────────────────────────────────────────────────────

/**
 * Etiquetas curtas, e só quando há o que dizer. Antes eram cinco parágrafos
 * empilhados entre o número e a lista — indispensáveis, mas não nesse formato.
 */
function QualityFlags({ summary }: { summary: LiveCartAudience['summary'] }) {
  const flags: Array<{ text: string; detail: string }> = []

  if (!summary.ga4.available) {
    flags.push({
      text: 'sem o Google Analytics',
      detail: 'A leitura do carrinho da loja falhou. Só aparece o que passou pelas telas da Widde; o que foi adicionado pelo botão da loja não está aqui.',
    })
  } else if (summary.ga4.rowsForLive === 0) {
    flags.push({
      text: 'a loja não envia adições ao carrinho',
      detail: 'O Google Analytics não devolveu nenhuma adição para este público. Em geral a loja não dispara o evento add_to_cart com os itens. Não significa que ninguém adicionou.',
    })
  }
  if (summary.ga4.available && summary.ga4.precision && summary.ga4.precision !== 'minute') {
    flags.push({
      text: 'horário aproximado',
      detail:
        summary.ga4.precision === 'hour'
          ? 'Esta conta do Google Analytics só entregou o carrinho por hora. Itens marcados com ~ estão situados pela hora; quando a hora cruza o início da live, usamos o horário em que a pessoa entrou na live (evento Widde).'
          : `Esta conta do Google Analytics só entregou o carrinho por ${summary.ga4.precision === 'day' ? 'dia' : 'período'}, sem hora. Itens do dia da live ficam como "horário não confirmado" e fora do carrinho da live.`,
    })
  }
  if (summary.ga4.truncatedByCardinality) {
    flags.push({
      text: 'faltam linhas',
      detail: 'O Google Analytics agrupou parte das combinações de pessoa e produto por volume. O que aparece está certo, mas a lista está incompleta.',
    })
  }
  if (summary.cartValueIsPartial) {
    flags.push({
      text: 'valores são um piso',
      detail: 'O Google Analytics só informa o valor de item comprado, então alguns itens entram sem preço e os totais em reais ficam abaixo do real.',
    })
  }
  if (summary.viewersTruncated) {
    flags.push({
      text: `só os ${summary.viewersListed} primeiros espectadores`,
      detail: `A live teve ${summary.viewers} espectadores e a lista foi cortada nos primeiros a chegar. Os números acima consideram só esses.`,
    })
  }

  if (flags.length === 0) return null

  return (
    <div style={{
      marginTop: SPACE.md, display: 'flex', gap: SPACE.xs, flexWrap: 'wrap', alignItems: 'center',
    }}>
      <span style={{ fontSize: 11, color: C.ink400 }}>Ressalvas:</span>
      {flags.map((f) => (
        <span
          key={f.text}
          title={f.detail}
          style={{
            fontSize: 11, fontWeight: 600, color: C.amber, background: C.amberSoft,
            border: `1px solid ${C.amber}33`, borderRadius: 20, padding: '2px 10px', cursor: 'help',
          }}
        >
          {f.text}
        </span>
      ))}
    </div>
  )
}

// ─── Barra de ferramentas ────────────────────────────────────────────────────

function Toolbar({ filter, counts, onFilter, search, onSearch, onExport, exportDisabled, exportOnlyAbandoned }: {
  filter: ViewerFilter
  counts: Record<ViewerFilter, number>
  onFilter: (f: ViewerFilter) => void
  search: string
  onSearch: (v: string) => void
  onExport: () => void
  exportDisabled: boolean
  exportOnlyAbandoned: boolean
}) {
  const tabs: Array<{ id: ViewerFilter; label: string; dot?: string }> = [
    { id: 'abandoned', label: 'Abandonaram', dot: C.amber },
    { id: 'recovered', label: 'Recuperaram', dot: C.green },
    { id: 'with-cart', label: 'Com carrinho' },
    { id: 'identified', label: 'Identificados' },
    { id: 'all', label: 'Todos' },
  ]

  return (
    <div style={{
      padding: `${SPACE.sm}px ${SPACE.xl}px`, background: C.surface,
      borderTop: `1px solid ${C.line}`, borderBottom: `1px solid ${C.line}`,
      display: 'flex', gap: SPACE.xs, alignItems: 'center', flexWrap: 'wrap',
    }}>
      {tabs.map((t) => (
        <FilterBtn key={t.id} active={filter === t.id} onClick={() => onFilter(t.id)} dot={t.dot}>
          {t.label} <span style={{ opacity: 0.7 }}>{counts[t.id]}</span>
        </FilterBtn>
      ))}
      <input
        type="search"
        placeholder="Buscar pessoa, telefone ou produto"
        value={search}
        onChange={(e) => onSearch(e.target.value)}
        style={{
          marginLeft: 'auto', minWidth: 230, padding: '5px 10px', fontSize: 12,
          border: `1px solid ${C.line}`, borderRadius: 6, outline: 'none', background: C.paper,
        }}
      />
      <button
        type="button"
        onClick={onExport}
        disabled={exportDisabled}
        title={exportOnlyAbandoned
          ? 'Baixa só os itens abandonados das pessoas listadas'
          : 'Baixa todos os itens das pessoas listadas'}
        style={{
          padding: '5px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: 'none',
          background: exportDisabled ? C.lineSoft : C.ink900,
          color: exportDisabled ? C.ink300 : '#fff',
          cursor: exportDisabled ? 'default' : 'pointer',
        }}
      >
        Baixar lista
      </button>
    </div>
  )
}

// ─── Lista ───────────────────────────────────────────────────────────────────

function ColumnHeader() {
  return (
    <div style={{
      display: 'grid', gridTemplateColumns: ROW_GRID, gap: SPACE.md,
      padding: `${SPACE.sm}px ${SPACE.xl}px ${SPACE.xs}px`,
      fontSize: 11, fontWeight: 600, color: C.ink400,
      borderBottom: `1px solid ${C.lineSoft}`,
      position: 'sticky', top: 0, background: C.paper, zIndex: 1,
    }}>
      <span />
      <span>Espectador</span>
      <span style={{ textAlign: 'right' }}>No carrinho</span>
      <span style={{ textAlign: 'right' }}>Abandonado</span>
      <span style={{ textAlign: 'right' }}>Comprou</span>
      <span />
    </div>
  )
}

function EmptyState({ hasViewers, summary }: { hasViewers: boolean; summary: LiveCartAudience['summary'] }) {
  const message = !hasViewers
    ? 'Ninguém desta live registrou visualização dentro da janela, então não há carrinho a mostrar.'
    : summary.ga4.rowsForLive === 0 && summary.ga4.available
      ? 'Ninguém neste filtro. A loja não está enviando as adições ao carrinho para o Google Analytics, então só aparece quem usou as telas da Widde.'
      : 'Ninguém neste filtro. Tente "Todos" ou limpe a busca.'

  return (
    <div style={{ padding: `${SPACE.xl}px`, fontSize: 12, color: C.ink500, textAlign: 'center', maxWidth: '60ch', margin: '0 auto', lineHeight: 1.6 }}>
      {message}
    </div>
  )
}

function ViewerRow({ viewer }: { viewer: LiveCartViewer }) {
  const [open, setOpen] = useState(false)
  const displayName = viewer.leadName || viewer.leadContact
  const inCart = viewer.items.filter((it) => it.quantity > 0)
  const abandoned = viewer.items.filter((it) => it.abandoned)
  const purchased = viewer.items.filter((it) => it.purchase)
  const recoveredCount = viewer.items.filter((it) => it.recovered).length
  const expandable = viewer.items.length > 0

  const cartMoney = money(inCart.length, viewer.cartTotal)
  const abandonedMoney = money(abandoned.length, viewer.abandonedValue)
  const purchasedMoney = money(purchased.length, viewer.purchasedFromCartRevenue)

  // Filete à esquerda em vez de fundo tingido: em dezenas de linhas o fundo
  // colorido vira ruído e apaga a hierarquia da seção.
  const marker = abandoned.length > 0 ? C.amber : recoveredCount > 0 ? C.green : 'transparent'

  return (
    <div style={{ borderBottom: `1px solid ${C.lineSoft}`, borderLeft: `3px solid ${marker}` }}>
      <div
        onClick={() => expandable && setOpen((o) => !o)}
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        onKeyDown={(e) => {
          if (!expandable) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setOpen((o) => !o)
          }
        }}
        style={{
          display: 'grid', gridTemplateColumns: ROW_GRID, gap: SPACE.md,
          padding: `${SPACE.sm}px ${SPACE.xl}px ${SPACE.sm}px ${SPACE.xl - 3}px`,
          alignItems: 'center', cursor: expandable ? 'pointer' : 'default',
        }}
      >
        <Avatar label={displayName ? initials(displayName) : '?'} identified={viewer.identified} />

        <div style={{ minWidth: 0 }}>
          <div style={{
            fontSize: 12, fontWeight: displayName ? 600 : 400,
            color: displayName ? C.ink900 : C.ink500,
            fontFamily: displayName ? undefined : 'var(--font-mono, monospace)',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }} title={viewer.widdeUid}>
            {displayName ? (
              <>
                {viewer.leadName}
                {viewer.leadName && viewer.leadContact && <span style={{ color: C.ink300, fontWeight: 400 }}> · {viewer.leadContact}</span>}
              </>
            ) : (
              `anônimo ${viewer.widdeUid.slice(0, 12)}…`
            )}
          </div>
          <div style={{ fontSize: 11, color: C.ink400, marginTop: 1 }}>
            entrou às {formatDateTime(viewer.firstViewAt).split(' ').slice(-1)[0]}
            {viewer.views > 1 && `, ${viewer.views} visualizações`}
            {viewer.ordersInWindow > 0 && `, ${plural(viewer.ordersInWindow, 'pedido', 'pedidos')} na janela`}
          </div>
        </div>

        {/* "No carrinho" é o que SOBROU; zero com itens significa que tudo saiu. */}
        <Amount
          text={cartMoney.text}
          detail={
            viewer.items.length === 0 ? 'nada registrado'
              : inCart.length === 0 ? 'nada sobrou'
                : items(inCart.length)
          }
          muted={cartMoney.empty}
          unknown={cartMoney.unknown}
        />
        <Amount
          text={abandonedMoney.text}
          detail={abandoned.length > 0 ? items(abandoned.length) : ''}
          color={abandoned.length > 0 ? C.amber : undefined}
          muted={abandonedMoney.empty}
          unknown={abandonedMoney.unknown}
          strong={abandoned.length > 0}
        />
        <Amount
          text={purchasedMoney.text}
          detail={recoveredCount > 0 ? plural(recoveredCount, 'recuperado', 'recuperados') : purchased.length > 0 ? items(purchased.length) : ''}
          color={purchased.length > 0 ? C.green : undefined}
          muted={purchasedMoney.empty}
          unknown={purchasedMoney.unknown}
        />

        <span style={{ fontSize: 10, color: C.ink300, textAlign: 'right' }}>{expandable ? (open ? '▾' : '▸') : ''}</span>
      </div>

      {open && expandable && <ItemTable items={viewer.items} />}
    </div>
  )
}

function Avatar({ label, identified }: { label: string; identified: boolean }) {
  return (
    <div style={{
      width: 28, height: 28, borderRadius: '50%',
      background: identified ? C.ink700 : 'transparent',
      border: identified ? 'none' : `1px dashed ${C.line}`,
      color: identified ? '#fff' : C.ink300,
      fontWeight: 600, fontSize: 10,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {label}
    </div>
  )
}

function Amount({ text, detail, color, muted, strong, unknown }: {
  text: string
  detail?: string
  color?: string
  muted?: boolean
  strong?: boolean
  /** Sem preço conhecido: texto menor e discreto, nunca com cara de valor. */
  unknown?: boolean
}) {
  return (
    <div style={{ textAlign: 'right', minWidth: 0 }}>
      <div style={{
        fontSize: unknown ? 10 : 12,
        fontWeight: unknown ? 400 : strong ? 700 : 600,
        color: unknown ? C.ink400 : muted ? C.ink300 : color ?? C.ink900,
        whiteSpace: unknown ? 'normal' : 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis',
        lineHeight: unknown ? 1.3 : undefined,
      }}>
        {text}
      </div>
      {detail && <div style={{ fontSize: 10, color: C.ink400, marginTop: 1 }}>{detail}</div>}
    </div>
  )
}

/** Detalhe por item — só na linha expandida, que é onde detalhe pertence. */
function ItemTable({ items }: { items: LiveCartItem[] }) {
  return (
    <div style={{ padding: `${SPACE.xs}px ${SPACE.xl}px ${SPACE.md}px ${SPACE.xl + 28 + SPACE.md - 3}px`, background: C.surface }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
        <thead>
          <tr style={{ color: C.ink400, textAlign: 'left' }}>
            <th style={th}>Item</th>
            <th style={{ ...th, textAlign: 'right', width: 80 }} title="Unidades que sobraram no carrinho">Qtd.</th>
            <th style={{ ...th, textAlign: 'right', width: 130 }}>Valor no carrinho</th>
            <th style={{ ...th, width: 170 }}>Entrou no carrinho</th>
            <th style={{ ...th, width: 80 }}>Visto por</th>
            <th style={{ ...th, width: 150 }}>Situação</th>
          </tr>
        </thead>
        <tbody>
          {items.map((it, i) => {
            const status = itemStatus(it)
            return (
              <tr key={`${it.productId}-${i}`} style={{ borderTop: `1px solid ${C.line}` }}>
                <td style={td}>
                  <div style={{ color: C.ink900, display: 'flex', alignItems: 'center', gap: SPACE.xs }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {it.productTitle || <em style={{ color: C.ink300 }}>sem título</em>}
                    </span>
                    {it.inLiveCatalog && (
                      <span
                        title="Este produto estava no catálogo da live"
                        style={{ fontSize: 9, fontWeight: 700, color: C.coral, border: `1px solid ${C.coral}`, borderRadius: 4, padding: '0 4px', flexShrink: 0 }}
                      >
                        live
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: C.ink300, fontFamily: 'var(--font-mono, monospace)' }}>{it.productId}</div>
                </td>
                <td style={{ ...td, textAlign: 'right' }}>
                  <div style={{ color: it.quantity === 0 ? C.ink300 : undefined }}>{it.quantity}</div>
                  {it.purchasedQuantity > 0 && (
                    <div style={{ fontSize: 10, color: C.green }}>{plural(it.purchasedQuantity, 'comprado', 'comprados')}</div>
                  )}
                </td>
                {/*
                  Valor do que SOBROU no carrinho. Item todo comprado sobra
                  zero — e escrever "R$ 0,00" ali afirmaria que o produto vale
                  zero, com a compra logo ao lado dizendo o contrário. Por isso
                  o traço, com o preço unitário embaixo para o número não sumir.
                */}
                <td style={{ ...td, textAlign: 'right' }}>
                  {it.priceSource === 'none' ? (
                    <span style={{ color: C.ink300 }} title="O Google Analytics só informa o valor de item comprado">
                      preço não informado
                    </span>
                  ) : it.quantity === 0 ? (
                    <>
                      <div style={{ color: C.ink300 }} title="Nada deste item sobrou no carrinho">—</div>
                      <div style={{ fontSize: 10, color: C.ink400 }}>{formatCurrency(it.unitPrice)} a unidade</div>
                    </>
                  ) : (
                    <>
                      <div>{formatCurrency(it.total)}</div>
                      {it.quantity > 1 && (
                        <div style={{ fontSize: 10, color: C.ink400 }}>{formatCurrency(it.unitPrice)} a unidade</div>
                      )}
                    </>
                  )}
                </td>
                <td style={td}>
                  <div>{it.firstAddedAt ? formatDateTime(it.firstAddedAt) : '—'}</div>
                  <div style={{ fontSize: 10, color: it.pendingBeforeLive ? C.amber : C.ink400 }}>
                    {phaseLabel(it)}
                    {SOURCE_LABEL[it.source] && `, ${SOURCE_LABEL[it.source]}`}
                  </div>
                </td>
                <td style={{ ...td, color: C.ink500 }}>{ORIGIN_LABEL[it.origin]}</td>
                <td style={td}>
                  <span style={{
                    display: 'inline-block', fontSize: 10, fontWeight: 600,
                    color: status.fg, background: status.bg, border: `1px solid ${status.fg}33`,
                    borderRadius: 20, padding: '1px 8px',
                  }}>
                    {status.label}
                  </span>
                  {it.purchase && (
                    <div style={{ fontSize: 10, color: C.ink400, marginTop: 2 }}>
                      {formatCurrency(it.purchase.itemRevenue)}
                      {it.purchase.dateHourMinute
                        ? ` em ${formatDateHourMinute(it.purchase.dateHourMinute)}`
                        : ', confirmado pelo Analytics'}
                    </div>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

const th: React.CSSProperties = { padding: `${SPACE.xs}px ${SPACE.sm}px`, fontWeight: 600, fontSize: 10 }
const td: React.CSSProperties = { padding: `${SPACE.sm}px`, verticalAlign: 'top' }

function FilterBtn({ active, onClick, dot, children }: {
  active: boolean
  onClick: () => void
  dot?: string
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5,
        padding: '4px 11px', fontSize: 11, borderRadius: 20, fontWeight: 600,
        background: active ? C.ink900 : C.paper,
        color: active ? '#fff' : C.ink700,
        border: `1px solid ${active ? C.ink900 : C.line}`,
        cursor: 'pointer',
      }}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: '50%', background: active ? '#fff' : dot }} />}
      {children}
    </button>
  )
}
